import {
  ApplicationConfig,
  ENVIRONMENT_INITIALIZER,
  importProvidersFrom,
  inject,
  provideZonelessChangeDetection,
} from '@angular/core';
import { SocketService } from '@core/services/socket.service';
import { SessionService } from '@core/services/session.service';
import { ChatService } from '@features/chat/services/chat.service';
import { CasesService } from '@features/cases/services/cases.service';
import { CaseBattlesService } from '@features/case-battles/case-battles.service';
import { LeaderboardService } from '@features/leaderboard/services/leaderboard.service';
import { LiveBetsService } from '@features/live-bets/services/live-bets.service';
import { RewardsService } from '@features/rewards/services/rewards.service';
import { DepositService } from '@features/deposit/services/deposit.service';
import { GamesService } from '@core/services/games.service';
import { PlinkoService } from '@old-app/plinko/plinko.service';
import { JackpotHrService } from '@old-app/jackpot/jackpot-hr.service';
import { UpgraderService } from '@old-app/upgrader/upgrader.service';
import { KingService } from '@old-app/king/king.service';
import { WheelService } from '@old-app/wheel/wheel.service';
import { TowersService } from '@old-app/towers/towers.service';
import { MinesService } from '@old-app/mines/mines.service';
import { AffiliatesService } from '@features/affiliates/services/affiliates.service';
import { GiveawaysService } from '@features/giveaways/giveaways.service';
import { WithdrawService } from '@features/withdraw/withdraw.service';
import { provideRouter, withViewTransitions } from '@angular/router';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideHttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { routes } from './app.routes';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { environment } from '../environments/environment';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideSharedModals } from '@features/modals/shared-modals.providers';
import { provideCoinflipModals } from '@features/coinflip/coinflip-modals.providers';
import { provideKingModals } from '@features/king/king-modals.providers';
import { provideJackpotModals } from '@features/jackpot/jackpot-modals.providers';
import { provideDepositModals } from '@features/deposit/deposit-modals.providers';
import { provideWithdrawModals } from '@features/withdraw/withdraw-modals.providers';
import { provideAffiliatesModals } from '@features/affiliates/affiliates-modals.providers';
import { provideCaseBattlesModals } from '@features/case-battles/case-battles-modals.providers';
import { provideSupportModals } from '@features/support/support-modals.providers';
import { provideRewardsModals } from '@features/rewards/rewards-modals.providers';
import { provideSupplyDropModals } from '@features/supply-drop/supply-drop-modals.providers';
import { provideUserModals } from '@features/user/user-modals.providers';
import { provideAdminModals } from '@features/admin/admin-modals.providers';
import { provideTriviaModals } from '@features/trivia/trivia-modals.providers';
import { provideRainModals } from '@features/rain/rain-modals.providers';
import { provideMinesModals } from '@features/mines/mines-modals.providers';
import { provideTowersModals } from '@features/towers/towers-modals.providers';
import { providePlinkoModals } from '@features/plinko/plinko-modals.providers';
import { provideUpgraderModals } from '@features/upgrader-new/upgrader-modals.providers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withViewTransitions()),
    provideHttpClient(),
    provideAngularSvgIcon(),
    provideAnimations(),
    provideToastr({
      positionClass: 'tf2hunt--toastr__bottom-right',
      toastClass: 'tf2hunt--toastr ngx-toastr',
      closeButton: true,
      autoDismiss: true,
      newestOnTop: false,
      maxOpened: 4,
    }),
    provideZonelessChangeDetection(),
    provideEnvironmentNgxMask(),
    importProvidersFrom(RecaptchaV3Module),
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaSiteKey,
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    provideCharts(withDefaultRegisterables()),
    ...provideSharedModals(),
    ...provideCoinflipModals(),
    ...provideKingModals(),
    ...provideJackpotModals(),
    ...provideDepositModals(),
    ...provideWithdrawModals(),
    ...provideAffiliatesModals(),
    ...provideCaseBattlesModals(),
    ...provideSupportModals(),
    ...provideRewardsModals(),
    ...provideSupplyDropModals(),
    ...provideUserModals(),
    ...provideAdminModals(),
    ...provideTriviaModals(),
    ...provideRainModals(),
    ...provideMinesModals(),
    ...provideTowersModals(),
    ...providePlinkoModals(),
    ...provideUpgraderModals(),
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        inject(SocketService);
        inject(SessionService);
        inject(ChatService);
        inject(CasesService);
        inject(CaseBattlesService);
        inject(LeaderboardService);
        inject(LiveBetsService);
        inject(RewardsService);
        inject(DepositService);
        inject(GamesService);
        inject(PlinkoService);
        inject(JackpotHrService);
        inject(UpgraderService);
        inject(KingService);
        inject(WheelService);
        inject(TowersService);
        inject(MinesService);
        inject(AffiliatesService);
        inject(GiveawaysService);
        inject(WithdrawService);
      },
    },
  ],
};
